/**
 * Closeout "Whose turn is it?" (Corbin said go 10/28; mockup
 * https://claude.ai/artifact/BEphPoeWBuo5pKfXYFxeHU).
 *
 *   WhoseTurnSwitch — "Try whose turn", per person, off until they flip it.
 *                     Shows only while who has the ball is on.
 *   DoneSendBack    — one tap on the card and the job header.
 *   TurnLanes       — On you now / Ready for anyone / Waiting (slice 3).
 *   TurnLine        — the face, the line and the one clock on a card.
 *   useClaim        — opening a pile job claims it; ClaimStrip says who has it.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import {
    BALL_AMBER,
    BALL_COLOR,
    HANDOFF_REPLY_COLOR,
    sinceLabel,
    useBallSwitch,
    useWhoseTurn,
    type BallHandoff,
    type Turn,
} from "@/lib/closeout-ball";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { HandToDialog } from "@/components/closeout/handoff";
import { HowItWorks, type HowStep } from "@/components/how-it-works";
import { CLOSE_OUT_QUEUE_KEY, refreshCloseOutQueue } from "@/pages/close-out/queueRefresh";

/** The ⓘ next to the switch (slice 6): how to read the new list. */
export const WHOSE_TURN_HOW: HowStep[] = [
    {
        title: "Read the face and the line",
        text: "The face is who has the job now. The line says what they need to do. The gray words say since when.",
        picture: "closeout-turn-line.png",
        alt: "A Closeout card with its bottom line ringed: a face, Ready for anyone, send packet, since Sat",
    },
    {
        title: "Work On you now from the top",
        text: "Green ones came back to you. Do those first. A job someone handed you moves up after 1 work day. An amber dot means it has been on you more than 2 work days.",
        picture: "closeout-turn-came-back.png",
        alt: "A card at the top of On you now with a green line ringed: Ready for Darcy, Corbin changed the payment terms",
    },
    {
        title: "Ready for anyone: open one and it's yours",
        text: "Nobody owns these yet. When you open one, it moves to you and everyone else sees that you're working on it. Leave without handing it off and it goes back to the pile. Stuck jobs wait on someone outside: press the one button on the card.",
    },
    {
        title: "Handing a job over? Tap what you need",
        text: "Pick the person, then tap the step you need. When it's done, the job comes back to you by itself. You don't have to chase it.",
        picture: "closeout-hand-to-asks.png",
        alt: "The Hand to window with Finalize the invoice ringed and the words GWC watches for it",
    },
];

export function WhoseTurnSwitch({ dark }: { dark?: boolean }) {
    const ball = useBallSwitch();
    const sw = useWhoseTurn();
    if (!ball.on) return null;
    return (
        <>
            <button
                type="button"
                onClick={() => sw.set(!sw.on)}
                title={
                    sw.on
                        ? "Back to Yours and Waiting. Only your view changes."
                        : "Hand-offs come back by themselves, and your list shows what's on you now. Only your view changes."
                }
                aria-pressed={sw.on}
                className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold",
                    dark
                        ? "border-white/25 text-shell-foreground/90 hover:bg-white/10"
                        : "border-border text-foreground/75 hover:bg-muted",
                )}
                data-testid="whose-turn-switch"
            >
                <span
                    className="h-2 w-2 rounded-full"
                    style={{
                        background: sw.on ? BALL_COLOR.approver : "transparent",
                        border: `1.5px solid ${BALL_COLOR.approver}`,
                    }}
                />
                {sw.on ? "Whose turn: on" : "Try whose turn"}
            </button>
            {sw.on && (
                <HowItWorks
                    title="How whose turn works"
                    steps={WHOSE_TURN_HOW}
                    testId="how-closeout-whose-turn"
                    className="h-5 w-5 text-[11px]"
                />
            )}
        </>
    );
}

/**
 * "Done, send it back" — one tap, no note (10/28 pick 1). Only for a
 * hand-off GWC can't watch ("Something else", or an older typed one), and
 * only for the person it was handed to. A watched step has no button: it
 * goes back by itself.
 */
export function DoneSendBack({
    jobId,
    handoff,
    viewerId,
    className,
}: {
    jobId: string;
    handoff: BallHandoff | null | undefined;
    viewerId: string | null;
    className?: string;
}) {
    const qc = useQueryClient();
    const turn = useWhoseTurn();
    const m = useMutation({
        mutationFn: (id: string) => api.post(`/api/close-out/handoffs/${id}/answer`, { outcome: "done" }),
        onSuccess: () => {
            toast.success(`Sent back to ${handoff?.fromName ?? "them"}.`);
            refreshCloseOutQueue(qc, { jobId });
            qc.invalidateQueries({ queryKey: ["close-out-handoff", jobId] });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't send it back. Try again."),
    });
    if (!turn.on || !handoff || handoff.state !== "open" || !viewerId || handoff.toUserId !== viewerId) return null;
    if (handoff.askKind && handoff.askKind !== "other") return null;
    return (
        <Button
            size="sm"
            className={cn("h-7 bg-emerald-700 px-3 text-[12px] font-bold text-white hover:bg-emerald-800", className)}
            disabled={m.isPending}
            onClick={(e) => {
                e.stopPropagation();
                m.mutate(handoff.id);
            }}
            data-testid="done-send-back"
        >
            <Check className="mr-1 h-3.5 w-3.5" aria-hidden />
            Done, send it back
        </Button>
    );
}

// ─── Slice 3: the lanes, the face, the one clock, claim on open ─────────

/** What each lane is called and says under its name (7th grade, no codes). */
export const LANE_TEXT = {
    on_you: { title: "On you now", sub: "Oldest first. Something handed to you goes to the top after 1 work day." },
    pile: { title: "Ready for anyone", sub: "Nobody has these yet. Open one and it's yours." },
    stuck: { title: "Stuck, needs a nudge", sub: "Old because someone outside hasn't moved. One action each." },
    waiting: { title: "Waiting on someone else", sub: "Nothing for you to do. Tap to see who has each one." },
} as const;

interface TurnJob {
    id: string;
    turn?: Turn | null;
}

function stamp(iso: string | null | undefined): number {
    const t = iso ? new Date(iso).getTime() : NaN;
    return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
}

export interface WaitingGroup<J> {
    key: string;
    who: string;
    jobs: J[];
}

/**
 * Split the list into the lanes (pick 3 rise rule): came back first (until
 * opened), then jobs handed to you a full work day ago, then the rest of
 * yours oldest first. The pile is oldest first. Waiting is grouped by face.
 * Every job is in exactly one place (no duplicate cards).
 */
export function splitByTurn<J extends TurnJob>(
    jobs: J[],
): { onYou: J[]; pile: J[]; stuck: J[]; waiting: Array<WaitingGroup<J>>; cameBack: number } {
    const onYou: J[] = [];
    const pile: J[] = [];
    const stuck: J[] = [];
    const groups = new Map<string, WaitingGroup<J>>();
    for (const j of jobs) {
        const t = j.turn;
        if (!t) continue;
        if (t.lane === "came_back" || t.lane === "on_you") onYou.push(j);
        else if (t.lane === "pile") pile.push(j);
        else if (t.lane === "stuck") stuck.push(j);
        else {
            const who = t.who ?? "No one";
            const key = who.toLowerCase();
            let g = groups.get(key);
            if (!g) {
                g = { key, who: who.charAt(0).toUpperCase() + who.slice(1), jobs: [] };
                groups.set(key, g);
            }
            g.jobs.push(j);
        }
    }
    const rank = (t: Turn) => (t.lane === "came_back" ? 0 : t.rise ? 1 : 2);
    onYou.sort(
        (a, b) =>
            rank(a.turn!) - rank(b.turn!) || stamp(a.turn!.since) - stamp(b.turn!.since) || (a.id < b.id ? -1 : 1),
    );
    pile.sort((a, b) => stamp(a.turn!.since) - stamp(b.turn!.since) || (a.id < b.id ? -1 : 1));
    stuck.sort((a, b) => stamp(a.turn!.since) - stamp(b.turn!.since) || (a.id < b.id ? -1 : 1));
    for (const g of groups.values()) g.jobs.sort((a, b) => stamp(a.turn!.since) - stamp(b.turn!.since));
    const waiting = [...groups.values()].sort((a, b) => b.jobs.length - a.jobs.length || a.who.localeCompare(b.who));
    return { onYou, pile, stuck, waiting, cameBack: onYou.filter((j) => j.turn!.lane === "came_back").length };
}

const FACE_BG: Record<string, string> = {
    came_back: HANDOFF_REPLY_COLOR,
    on_you: BALL_COLOR.office,
    pile: "#9a9a92",
    stuck: "#8a8a83",
    waiting: "#64748b",
};

/** A round face with the first letter, or a dotted ring for the pile. */
export function Face({ turn, size = 18 }: { turn: Turn; size?: number }) {
    const letter = (turn.who ?? "").trim().charAt(0).toUpperCase();
    if (turn.lane === "pile" || !letter) {
        return (
            <span
                className="flex flex-none items-center justify-center rounded-full border-[1.5px] border-dashed"
                style={{ width: size, height: size, borderColor: FACE_BG.pile }}
                aria-hidden
            />
        );
    }
    return (
        <span
            className="flex flex-none items-center justify-center rounded-full font-extrabold text-white"
            style={{ width: size, height: size, fontSize: size * 0.5, background: FACE_BG[turn.lane] }}
            aria-hidden
        >
            {letter}
        </span>
    );
}

/** The one line under a card: face, what's next, and a gray "since Tue". */
export function TurnLine({ turn }: { turn: Turn }) {
    return (
        <div
            className="mt-1 flex items-center gap-1.5 border-t border-dashed border-border pt-1 text-[11px] font-bold"
            style={{
                color:
                    turn.lane === "came_back"
                        ? HANDOFF_REPLY_COLOR
                        : turn.lane === "on_you"
                          ? BALL_COLOR.office
                          : "#4a4c52",
            }}
            data-testid="turn-line"
            title={turn.line}
        >
            <Face turn={turn} />
            <span className="min-w-0 truncate">{turn.line}</span>
            <span className="ml-auto flex flex-none items-center gap-1 font-semibold text-foreground/50">
                {turn.amber && (
                    <span
                        className="h-[7px] w-[7px] rounded-full"
                        style={{ background: BALL_AMBER }}
                        aria-label="on you more than 2 work days"
                        data-testid="turn-amber"
                    />
                )}
                {sinceLabel(turn.since)}
            </span>
        </div>
    );
}

/** The first chip of NEEDED TO FINISH with whose turn on: the same face,
 *  line and clock as the card (no "3 days" counter). */
export function TurnChip({ turn, onClick }: { turn: Turn; onClick?: () => void }) {
    const color =
        turn.lane === "came_back" ? HANDOFF_REPLY_COLOR : turn.lane === "on_you" ? BALL_COLOR.office : "#4a4c52";
    return (
        <button
            type="button"
            onClick={onClick}
            className="inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-left text-[10.5px] font-extrabold"
            style={{ color, background: `${color}1f` }}
            data-testid="turn-chip"
        >
            <Face turn={turn} size={15} />
            <span className="min-w-0 truncate">{turn.line}</span>
            <span className="inline-flex items-center gap-1 font-semibold opacity-70">
                {turn.amber && <span className="h-[7px] w-[7px] rounded-full" style={{ background: BALL_AMBER }} />}
                {sinceLabel(turn.since)}
            </span>
        </button>
    );
}

function LaneHead({ title, count, sub, dark }: { title: string; count: number; sub?: string; dark?: boolean }) {
    return (
        <div className="mb-1.5">
            <div
                className={cn(
                    "flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[.06em]",
                    dark ? "text-white" : "text-foreground",
                )}
            >
                <span>{title}</span>
                <span className={cn("rounded-full px-1.5 text-[10px] font-bold", dark ? "bg-white/15" : "bg-muted")}>
                    {count}
                </span>
            </div>
            {sub && <div className={cn("text-[11px]", dark ? "text-white/60" : "text-foreground/60")}>{sub}</div>}
        </div>
    );
}

/**
 * The list, by whose turn it is (slice 3). `grid` = the main Closeout list;
 * `rail` = the side queue on wide screens. Waiting starts folded.
 */
export function TurnLanes<J extends TurnJob>({
    jobs,
    renderCard,
    variant = "grid",
    selectedId,
}: {
    jobs: J[];
    renderCard: (j: J) => React.ReactNode;
    variant?: "grid" | "rail";
    selectedId?: string;
}) {
    const { onYou, pile, stuck, waiting } = splitByTurn(jobs);
    const [openWaiting, setOpenWaiting] = useState<Record<string, boolean>>({});
    const [showWaiting, setShowWaiting] = useState(false);
    const waitingCount = waiting.reduce((n, g) => n + g.jobs.length, 0);
    const box = variant === "grid" ? "grid grid-cols-1 gap-2 md:grid-cols-2 2xl:grid-cols-3" : "";
    const list = (js: J[]) => (
        <div className={box}>
            {js.map((j) => (
                <div key={j.id}>{renderCard(j)}</div>
            ))}
        </div>
    );
    return (
        <div className="space-y-4" data-testid="turn-lanes">
            <section data-testid="lane-on-you">
                <LaneHead title={LANE_TEXT.on_you.title} count={onYou.length} sub={LANE_TEXT.on_you.sub} dark />
                {onYou.length === 0 ? (
                    <p className="text-[12px] text-white/60">Nothing is on you right now.</p>
                ) : (
                    list(onYou)
                )}
            </section>
            <section data-testid="lane-pile">
                <LaneHead title={LANE_TEXT.pile.title} count={pile.length} sub={LANE_TEXT.pile.sub} dark />
                {pile.length > 0 && list(pile)}
            </section>
            {stuck.length > 0 && (
                <section data-testid="lane-stuck">
                    <LaneHead title={LANE_TEXT.stuck.title} count={stuck.length} sub={LANE_TEXT.stuck.sub} dark />
                    {list(stuck)}
                </section>
            )}
            <section data-testid="lane-waiting">
                <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setShowWaiting((v) => !v)}
                    aria-expanded={showWaiting}
                    data-testid="lane-waiting-toggle"
                >
                    <LaneHead
                        title={`${showWaiting ? "▾" : "▸"} ${LANE_TEXT.waiting.title}`}
                        count={waitingCount}
                        sub={LANE_TEXT.waiting.sub}
                        dark
                    />
                </button>
                {(showWaiting || waiting.some((g) => g.jobs.some((j) => j.id === selectedId))) &&
                    waiting.map((g) => {
                        const open = !!openWaiting[g.key] || g.jobs.some((j) => j.id === selectedId);
                        return (
                            <div key={g.key} className="mb-2">
                                <button
                                    type="button"
                                    onClick={() => setOpenWaiting((o) => ({ ...o, [g.key]: !o[g.key] }))}
                                    className="mb-1 flex items-center gap-1.5 text-[11.5px] font-bold text-white/80"
                                    aria-expanded={open}
                                >
                                    <Face turn={g.jobs[0].turn!} size={16} />
                                    {g.who} · {g.jobs.length}
                                </button>
                                {open && list(g.jobs)}
                            </div>
                        );
                    })}
            </section>
        </div>
    );
}

/**
 * Claim on open (pick 4). Opening a job from the pile (or one you already
 * have) claims it on the server and keeps it alive every 4 minutes while it
 * is open. Leaving it releases it. If someone else has it, says who.
 */
export function useClaim(jobId: string, turn: Turn | null | undefined, enabled: boolean): string | null {
    const qc = useQueryClient();
    const [heldBy, setHeldBy] = useState<string | null>(null);
    const claimable =
        enabled &&
        !!turn &&
        (turn.lane === "pile" || (turn.lane === "on_you" && !!turn.stepKey && turn.line.startsWith("On you — ")));
    const stepKey = turn?.stepKey ?? null;
    useEffect(() => {
        setHeldBy(null);
        if (!claimable || !jobId) return;
        let alive = true;
        let mine = false;
        const ping = () =>
            api
                .post<{ mine: boolean; claimedBy: { name: string } | null }>(`/api/close-out/jobs/${jobId}/claim`, {
                    stepKey,
                })
                .then((r) => {
                    if (!alive) return;
                    if (r?.mine && !mine) refreshCloseOutQueue(qc, { jobId });
                    mine = !!r?.mine;
                    setHeldBy(r?.mine ? null : (r?.claimedBy?.name ?? null));
                })
                .catch(() => undefined);
        void ping();
        const t = setInterval(ping, 4 * 60 * 1000);
        return () => {
            alive = false;
            clearInterval(t);
            if (mine) {
                api.post(`/api/close-out/jobs/${jobId}/release`, {})
                    .then(() => refreshCloseOutQueue(qc, { jobId }))
                    .catch(() => undefined);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, claimable, stepKey]);
    return heldBy ?? (enabled && turn?.claimedBy ? turn.claimedBy.name : null);
}

/** "Regina is working on this" under the job name. */
export function ClaimStrip({ name }: { name: string | null }) {
    if (!name) return null;
    return (
        <div
            className="flex items-center gap-2 border-b border-border bg-slate-50 px-3.5 py-1.5 text-[12.5px] font-semibold dark:bg-slate-900/40"
            data-testid="claim-strip"
        >
            <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-slate-500 text-[9px] font-extrabold text-white">
                {name.charAt(0).toUpperCase()}
            </span>
            {name} is working on this. You can look, but leave it to them.
        </div>
    );
}

/** "On you now" for the toolbar: read from the Closeout list already loaded. */
export function onYouCount(jobs: TurnJob[] | null | undefined): number {
    return (jobs ?? []).filter((j) => j.turn?.lane === "on_you" || j.turn?.lane === "came_back").length;
}

/**
 * The toolbar's Closeout number for someone trying whose turn: what's on
 * them now (pick 6), read from the Closeout list they already loaded. Null
 * = not trying it, or the list isn't loaded yet (the toolbar keeps its own
 * count). Never fetches the list by itself.
 */
export function useCloseoutOnYou(enabled: boolean): number | null {
    const turn = useWhoseTurn(enabled);
    const q = useQuery({
        queryKey: CLOSE_OUT_QUEUE_KEY,
        queryFn: () => api.get<{ jobs: TurnJob[] }>("/api/close-out/queue"),
        enabled: false,
    });
    if (!enabled || !turn.on || !q.data?.jobs) return null;
    if (!q.data.jobs.some((j) => !!j.turn)) return null;
    return onYouCount(q.data.jobs);
}

// ─── Slice 4: the Today line replaces the scoreboard (pick 6) ───────────

/** The counts behind the Today line. Pure, so the numbers are testable. */
export function todayCounts(jobs: TurnJob[], done: number) {
    const s = splitByTurn(jobs);
    return {
        onYou: s.onYou.length - s.cameBack,
        cameBack: s.cameBack,
        pile: s.pile.length,
        stuck: s.stuck.length,
        waiting: s.waiting.reduce((n, g) => n + g.jobs.length, 0),
        done,
    };
}

/**
 * One calm line at the top of Closeout: what's on you, what came back, the
 * shared pile, what waits on others, what you moved today. No goal, no
 * streak, no medals, no confetti (Corbin: "it should feel like relief").
 */
export function TodayLine({ jobs, done }: { jobs: TurnJob[]; done: number }) {
    const c = todayCounts(jobs, done);
    const part = (n: number, words: string, color?: string, testId?: string) => (
        <span className="inline-flex items-baseline gap-1" data-testid={testId}>
            <b className="text-[17px] font-extrabold" style={color ? { color } : undefined}>
                {n}
            </b>
            <span className="text-white/75">{words}</span>
        </span>
    );
    return (
        <div
            className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-[13px] px-4 py-3 text-[13px] text-white"
            style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.08)" }}
            data-testid="today-line"
        >
            <span className="mr-1 text-[11px] font-extrabold uppercase tracking-[.12em] text-white/55">Today</span>
            {part(c.onYou, "on you", "#5eead4", "today-on-you")}
            <span className="text-white/30">·</span>
            {part(c.cameBack, "came back", "#86efac", "today-came-back")}
            <span className="text-white/30">·</span>
            {part(c.pile, "ready for anyone", undefined, "today-pile")}
            <span className="text-white/30">·</span>
            {part(c.stuck, "stuck", undefined, "today-stuck")}
            <span className="text-white/30">·</span>
            {part(c.waiting, "waiting on others", undefined, "today-waiting")}
            <span className="text-white/30">·</span>
            {part(c.done, "done today", undefined, "today-done")}
        </div>
    );
}

// ─── Slice 5: the one nudge on a stuck card ─────────────────────────────

/**
 * One button on a Stuck card. Most open the job at the step where the nudge
 * happens. A tech's nudge is a hand-off to Corbin: messages to techs still
 * need his OK (pick 7).
 */
export function StuckAction({
    jobId,
    jobName,
    turn,
    approverId,
    onOpenAt,
}: {
    jobId: string;
    jobName: string;
    turn: Turn | null | undefined;
    approverId?: string | null;
    onOpenAt: (station: number) => void;
}) {
    const [handing, setHanding] = useState(false);
    const a = turn?.lane === "stuck" ? turn.stuck?.action : null;
    if (!a) return null;
    const viaCorbin = a.kind === "hand_to_approver" && !!approverId;
    return (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <Button
                size="sm"
                variant="outline"
                className="mt-1.5 h-7 px-3 text-[12px] font-bold"
                onClick={() => (viaCorbin ? setHanding(true) : onOpenAt(a.station))}
                data-testid="stuck-action"
            >
                {a.label}
            </Button>
            {handing && approverId && (
                <HandToDialog
                    jobId={jobId}
                    jobName={jobName}
                    defaultToUserId={approverId}
                    asks={[{ kind: "other", label: "Something else" }]}
                    onClose={() => setHanding(false)}
                />
            )}
        </div>
    );
}
